import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
    Loader2,
    LogOut,
    Mail,
    MessageCircle,
    RefreshCw,
    Calendar,
    Inbox,
    ExternalLink,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "momia_admin_pw";

function fmtDate(s) {
    try {
        const d = new Date(s);
        return d.toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return String(s);
    }
}

function Login({ onAuth }) {
    const [pw, setPw] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            await axios.post(`${API}/admin/login`, { password: pw });
            sessionStorage.setItem(STORAGE_KEY, pw);
            onAuth(pw);
        } catch (e) {
            setErr(
                e?.response?.status === 401
                    ? "Incorrect password"
                    : "Login failed — try again"
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#FAF9F6] grid place-items-center px-6">
            <form
                onSubmit={submit}
                className="bg-white border border-stone-200 p-8 md:p-10 w-full max-w-md"
                data-testid="admin-login-form"
            >
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-[#0C4A6E] text-white grid place-items-center font-cabinet font-black text-lg">
                        M
                    </div>
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                            Admin Console
                        </div>
                        <div className="font-cabinet font-bold text-xl leading-tight">
                            Momia Healthcare Exports
                        </div>
                    </div>
                </div>
                <label className="field-label">Admin Password</label>
                <input
                    autoFocus
                    type="password"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    className="field-input"
                    placeholder="Enter password"
                    data-testid="admin-password-input"
                />
                {err && (
                    <div
                        className="mt-4 p-3 border border-red-300 bg-red-50 text-red-700 text-sm"
                        data-testid="admin-login-error"
                    >
                        {err}
                    </div>
                )}
                <button
                    type="submit"
                    disabled={loading || !pw}
                    className="btn-primary w-full justify-center mt-6 disabled:opacity-60"
                    data-testid="admin-login-submit"
                >
                    {loading ? (
                        <>
                            <Loader2 size={18} className="animate-spin" /> Signing in
                        </>
                    ) : (
                        "Sign in"
                    )}
                </button>
                <div className="mt-6 text-xs text-stone-500 font-mono uppercase tracking-[0.18em]">
                    Restricted access
                </div>
            </form>
        </div>
    );
}

function StatCard({ label, value, testid }) {
    return (
        <div
            className="bg-white border border-stone-200 p-6"
            data-testid={testid}
        >
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                {label}
            </div>
            <div className="font-cabinet font-black text-4xl mt-2 text-ink tracking-tight">
                {value}
            </div>
        </div>
    );
}

function InquiryRow({ it }) {
    return (
        <tr className="border-t border-stone-200 hover:bg-stone-50 align-top">
            <td className="p-4 text-xs font-mono text-stone-500 whitespace-nowrap">
                {fmtDate(it.created_at)}
            </td>
            <td className="p-4">
                <div className="font-cabinet font-bold text-ink">{it.name}</div>
                {it.company && (
                    <div className="text-xs text-stone-500">{it.company}</div>
                )}
                <div className="text-xs text-stone-600 mt-1">{it.country}</div>
            </td>
            <td className="p-4 text-sm">
                <a
                    href={`mailto:${it.email}`}
                    className="text-[#0C4A6E] hover:underline flex items-center gap-1"
                >
                    <Mail size={14} strokeWidth={1.5} /> {it.email}
                </a>
                {it.phone && (
                    <a
                        href={`https://wa.me/${it.phone.replace(/[^\d]/g, "")}`}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1 text-[#4D7C0F] hover:underline flex items-center gap-1 text-xs"
                    >
                        <MessageCircle size={14} strokeWidth={1.5} /> {it.phone}
                    </a>
                )}
            </td>
            <td className="p-4 text-sm text-stone-700">
                {it.category || <span className="text-stone-400">—</span>}
            </td>
            <td className="p-4 text-sm text-stone-700 max-w-md">
                <div className="line-clamp-3 whitespace-pre-wrap">
                    {it.message}
                </div>
                {it.products && (
                    <div className="mt-2 text-xs text-stone-500 font-mono">
                        {it.products}
                    </div>
                )}
            </td>
        </tr>
    );
}

function MeetingRow({ it }) {
    return (
        <tr className="border-t border-stone-200 hover:bg-stone-50 align-top">
            <td className="p-4 text-xs font-mono text-stone-500 whitespace-nowrap">
                {fmtDate(it.created_at)}
            </td>
            <td className="p-4">
                <div className="font-cabinet font-bold text-ink">{it.name}</div>
                {it.company && (
                    <div className="text-xs text-stone-500">{it.company}</div>
                )}
            </td>
            <td className="p-4 text-sm">
                <a
                    href={`mailto:${it.email}`}
                    className="text-[#0C4A6E] hover:underline flex items-center gap-1"
                >
                    <Mail size={14} strokeWidth={1.5} /> {it.email}
                </a>
                {it.phone && (
                    <a
                        href={`https://wa.me/${it.phone.replace(/[^\d]/g, "")}`}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1 text-[#4D7C0F] hover:underline flex items-center gap-1 text-xs"
                    >
                        <MessageCircle size={14} strokeWidth={1.5} />{" "}
                        {it.phone}
                    </a>
                )}
            </td>
            <td className="p-4 text-sm text-stone-700">
                <div className="font-cabinet font-bold">{it.preferred_date}</div>
                <div className="text-xs">
                    {it.preferred_time} · {it.timezone_str || "IST"}
                </div>
            </td>
            <td className="p-4 text-sm text-stone-700 max-w-md">
                <div className="font-medium">{it.topic || "—"}</div>
                {it.notes && (
                    <div className="mt-1 text-xs text-stone-500 line-clamp-3 whitespace-pre-wrap">
                        {it.notes}
                    </div>
                )}
            </td>
        </tr>
    );
}

function Dashboard({ pw, onLogout }) {
    const [tab, setTab] = useState("inquiries");
    const [inquiries, setInquiries] = useState([]);
    const [meetings, setMeetings] = useState([]);
    const [summary, setSummary] = useState({
        inquiries_total: 0,
        meetings_total: 0,
    });
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const fetchAll = useCallback(async () => {
        setLoading(true);
        setErr("");
        try {
            const headers = { "X-Admin-Password": pw };
            const [s, i, m] = await Promise.all([
                axios.get(`${API}/admin/summary`, { headers }),
                axios.get(`${API}/admin/inquiries`, { headers }),
                axios.get(`${API}/admin/meetings`, { headers }),
            ]);
            setSummary(s.data);
            setInquiries(i.data);
            setMeetings(m.data);
        } catch (e) {
            if (e?.response?.status === 401) {
                onLogout();
                return;
            }
            setErr("Failed to load data. Please refresh.");
        } finally {
            setLoading(false);
        }
    }, [pw, onLogout]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    return (
        <div className="min-h-screen bg-[#FAF9F6]">
            <header
                className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-stone-200"
                data-testid="admin-header"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-[#0C4A6E] text-white grid place-items-center font-cabinet font-black text-sm">
                            M
                        </div>
                        <div>
                            <div className="font-cabinet font-bold text-ink text-sm leading-tight">
                                Momia · Admin
                            </div>
                            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-stone-500">
                                Inquiries & Meetings
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <a
                            href="/"
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-stone-600 hover:text-ink px-3 py-2"
                            data-testid="admin-view-site"
                        >
                            <ExternalLink size={14} strokeWidth={1.5} /> View site
                        </a>
                        <button
                            onClick={fetchAll}
                            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-stone-600 hover:text-ink border border-stone-200 px-3 py-2"
                            data-testid="admin-refresh"
                        >
                            <RefreshCw
                                size={14}
                                strokeWidth={1.5}
                                className={loading ? "animate-spin" : ""}
                            />
                            Refresh
                        </button>
                        <button
                            onClick={onLogout}
                            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-white bg-ink hover:bg-black px-3 py-2"
                            data-testid="admin-logout"
                        >
                            <LogOut size={14} strokeWidth={1.5} /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
                <div className="mb-8">
                    <div className="overline mb-2">Dashboard</div>
                    <h1 className="font-cabinet font-black text-4xl md:text-5xl tracking-tight text-ink">
                        Inbox
                    </h1>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                    <StatCard
                        label="Total Inquiries"
                        value={summary.inquiries_total}
                        testid="stat-inq-total"
                    />
                    <StatCard
                        label="Total Meetings"
                        value={summary.meetings_total}
                        testid="stat-meet-total"
                    />
                    <StatCard
                        label="Showing"
                        value={
                            tab === "inquiries"
                                ? inquiries.length
                                : meetings.length
                        }
                        testid="stat-showing"
                    />
                    <StatCard
                        label="Last refresh"
                        value={new Date().toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                        testid="stat-refresh-time"
                    />
                </div>

                {err && (
                    <div
                        className="mb-6 p-4 border border-red-300 bg-red-50 text-red-700 text-sm"
                        data-testid="admin-error"
                    >
                        {err}
                    </div>
                )}

                <div className="flex gap-1 mb-6 border-b border-stone-200">
                    <button
                        onClick={() => setTab("inquiries")}
                        className={`px-5 py-3 font-mono text-xs uppercase tracking-[0.18em] border-b-2 ${tab === "inquiries" ? "border-[#0C4A6E] text-ink" : "border-transparent text-stone-500 hover:text-ink"}`}
                        data-testid="tab-inquiries"
                    >
                        <Inbox
                            size={14}
                            strokeWidth={1.5}
                            className="inline mr-2 -mt-0.5"
                        />
                        Inquiries ({inquiries.length})
                    </button>
                    <button
                        onClick={() => setTab("meetings")}
                        className={`px-5 py-3 font-mono text-xs uppercase tracking-[0.18em] border-b-2 ${tab === "meetings" ? "border-[#0C4A6E] text-ink" : "border-transparent text-stone-500 hover:text-ink"}`}
                        data-testid="tab-meetings"
                    >
                        <Calendar
                            size={14}
                            strokeWidth={1.5}
                            className="inline mr-2 -mt-0.5"
                        />
                        Meetings ({meetings.length})
                    </button>
                </div>

                {loading ? (
                    <div
                        className="py-20 grid place-items-center text-stone-500"
                        data-testid="admin-loading"
                    >
                        <Loader2 size={28} className="animate-spin" />
                    </div>
                ) : tab === "inquiries" ? (
                    <div
                        className="bg-white border border-stone-200 overflow-x-auto"
                        data-testid="inquiries-table"
                    >
                        {inquiries.length === 0 ? (
                            <div className="p-10 text-center text-stone-500">
                                No inquiries yet.
                            </div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-stone-50 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500">
                                        <th className="p-4">Received</th>
                                        <th className="p-4">Buyer</th>
                                        <th className="p-4">Contact</th>
                                        <th className="p-4">Category</th>
                                        <th className="p-4">Message</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {inquiries.map((it) => (
                                        <InquiryRow key={it.id} it={it} />
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                ) : (
                    <div
                        className="bg-white border border-stone-200 overflow-x-auto"
                        data-testid="meetings-table"
                    >
                        {meetings.length === 0 ? (
                            <div className="p-10 text-center text-stone-500">
                                No meeting requests yet.
                            </div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-stone-50 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500">
                                        <th className="p-4">Requested</th>
                                        <th className="p-4">Requester</th>
                                        <th className="p-4">Contact</th>
                                        <th className="p-4">Slot</th>
                                        <th className="p-4">Topic & Notes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {meetings.map((it) => (
                                        <MeetingRow key={it.id} it={it} />
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
}

export default function Admin() {
    const [pw, setPw] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");

    const handleLogout = () => {
        sessionStorage.removeItem(STORAGE_KEY);
        setPw("");
    };

    if (!pw) return <Login onAuth={setPw} />;
    return <Dashboard pw={pw} onLogout={handleLogout} />;
}
